/**
 * AlertTicker — Scrolling real-time alert feed for ambient dashboard bottom bar.
 *
 * DRR-183 Phase 3: Persistent alert ticker showing recent HIGH+ severity events.
 * CRITICAL events are highlighted with red background.
 * Auto-scrolls to latest alert. Configurable max visible items.
 */

import { useEffect, useRef } from 'react';
import { Group, Text, Badge, ScrollArea, Box, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconBell } from '@tabler/icons-react';

export interface AlertTickerItem {
  id: string;
  message: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM';
  timestamp: Date;
}

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: 'red',
  HIGH: 'orange',
  MEDIUM: 'yellow',
};

interface AlertTickerProps {
  alerts: AlertTickerItem[];
  maxVisible?: number;
}

export function AlertTicker({ alerts, maxVisible = 50 }: AlertTickerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const displayAlerts = alerts.slice(-maxVisible);

  // Auto-scroll to latest alert
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [displayAlerts.length]);

  if (displayAlerts.length === 0) {
    return (
      <Box
        p="xs"
        style={{
          borderTop: '1px solid var(--mantine-color-dark-4)',
          background: 'var(--mantine-color-dark-8)',
        }}
      >
        <Group gap="xs" justify="center">
          <ThemeIcon size="sm" variant="transparent" c="dimmed">
            <IconBell size={14} />
          </ThemeIcon>
          <Text size="xs" c="dimmed">
            No recent alerts
          </Text>
        </Group>
      </Box>
    );
  }

  return (
    <Box
      style={{
        borderTop: '1px solid var(--mantine-color-dark-4)',
        background: 'var(--mantine-color-dark-8)',
      }}
    >
      <ScrollArea
        scrollbarSize={4}
        type="hover"
        viewportRef={scrollRef}
        style={{ width: '100%' }}
      >
        <Group gap="sm" p="xs" wrap="nowrap" style={{ minWidth: 'max-content' }}>
          <ThemeIcon size="sm" variant="transparent" c="dimmed">
            <IconBell size={14} />
          </ThemeIcon>
          {displayAlerts.map((alert) => (
            <Badge
              key={alert.id}
              color={SEVERITY_COLORS[alert.severity] ?? 'gray'}
              variant={alert.severity === 'CRITICAL' ? 'filled' : 'light'}
              size="lg"
              radius="sm"
              leftSection={
                alert.severity === 'CRITICAL' ? (
                  <IconAlertTriangle size={12} />
                ) : undefined
              }
              style={{ flexShrink: 0 }}
            >
              <Text size="xs" span>
                {alert.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Text>
              {' '}
              <Text size="xs" span fw={500}>
                {alert.message}
              </Text>
            </Badge>
          ))}
        </Group>
      </ScrollArea>
    </Box>
  );
}
